import { OptionsApi, WatchLinkStatus } from '@core/swagger-client';
import { ContainerDefault, ErrorMessagePage, LoaderPage, Warning } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WebinarInfo } from '../../organisms';

export const WatchLinkPage = () => {
    const { t } = useTranslation();

    const { linkCode } = useParams({ strict: false });
    const navigate = useNavigate();

    const { data: webinar, error, isLoading } = useQuery(OptionsApi.watchLink.getWebinar({ linkCode: linkCode! }));

    useEffect(() => {
        if (!webinar) {
            return;
        }

        if (webinar.watchLinkStatus === WatchLinkStatus.Live) {
            navigate({ to: '/$webinarId/w/$linkCode', params: { webinarId: webinar.webinarId, linkCode: linkCode! } });
        }

        if (webinar.watchLinkStatus === WatchLinkStatus.OnDemand) {
            navigate({ to: '/$webinarId/o/$linkCode', params: { webinarId: webinar.webinarId, linkCode: linkCode! } });
        }
    }, [webinar]);

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    // Live and OnDemand redirect to the player (above): keep the loader page until then, also in the server HTML,
    // where the route's loader has already fetched the link.
    if (isLoading || !webinar || webinar.watchLinkStatus === WatchLinkStatus.Live || webinar.watchLinkStatus === WatchLinkStatus.OnDemand) {
        return <LoaderPage />;
    }

    return (
        <ContainerDefault>
            <div>
                <div className="max-w-4xl">
                    <WebinarInfo webinar={webinar} webinarAttachments={[]} />
                    <div className="sticky bottom-0 bg-accent-lighter rounded-webinarsub flex flex-col sm:flex-row gap-4 px-4 md:px-10 pt-2 pb-4 z-30">
                        {webinar.watchLinkStatus === WatchLinkStatus.AccessDenied && (
                            <div className="flex items-center mt-2">
                                <Warning classNames="w-6 mr-2" />
                                <span className="text-sm">{t('WebinarDetails.Warning.AccessRestricted')}</span>
                            </div>
                        )}
                        {webinar.watchLinkStatus === WatchLinkStatus.Expired && (
                            <div className="flex items-center mt-2">
                                <Warning classNames="w-6 mr-2" />
                                <span className="text-sm">{t('WebinarDetails.Warning.LinkExpired')}</span>
                            </div>
                        )}
                        {webinar.watchLinkStatus === WatchLinkStatus.Unavailable && (
                            <div className="flex items-center mt-2">
                                <Warning classNames="w-6 mr-2" />
                                <span className="text-sm">{t('WebinarDetails.Warning.OnDemandNotAvailable')}</span>
                            </div>
                        )}
                        {webinar.watchLinkStatus === WatchLinkStatus.Preparing && (
                            <div className="flex items-center mt-2">
                                <span className="text-sm">{t('WebinarDetails.Info.OnDemandPreparing')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ContainerDefault>
    );
};
